import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { PageLoader } from 'components/PageLoader';
import { EmptyState } from 'components/EmptyState';
import { Form, FormField, FormTextArea } from 'components/form';
import { GlobalScrollLock } from 'components/GlobalScrollLock';
import {
  emailSchema,
  nameSchema,
  optionalPhoneSchema,
  optionalPriceSchema,
} from 'utils/validators';

import { submitBooking } from '../../api';
import {
  fmtDate,
  fmtTime,
  formatServicePrice,
  groupSlotsByDate,
} from '../helpers';
import * as styled from '../styles/book-now';

type Props = {
  token: string;
  services: Kudable.Service[];
  currency: string | null;
  slotsData: Kudable.SlotsData;
  slotsLoading: boolean;
  accentColor: string;
  onClose: () => void;
};

const bookingSchema = z.object({
  client_name: nameSchema,
  client_phone: optionalPhoneSchema,
  client_email: emailSchema,
  client_whatsapp: optionalPhoneSchema,
  notes: z.string().max(1000, 'Too long'),
  suggested_price: optionalPriceSchema,
});
type BookingFormValues = z.infer<typeof bookingSchema>;

export const BookNowModal = ({
  token,
  services,
  currency,
  slotsData,
  slotsLoading,
  accentColor,
  onClose,
}: Props) => {
  const [step, setStep] = useState<Profile.BookingStep>('service');
  const [selectedService, setSelectedService] = useState<Kudable.Service | null>(
    null,
  );
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [completionCode, setCompletionCode] = useState<string | null>(null);
  const [repeatWeekly, setRepeatWeekly] = useState(false);

  const form = useForm<BookingFormValues>({
    resolver: zodResolver(bookingSchema),
    mode: 'onBlur',
    defaultValues: {
      client_name: '',
      client_phone: '',
      client_email: '',
      client_whatsapp: '',
      notes: '',
      suggested_price: '',
    },
  });

  const dateGroups = groupSlotsByDate(slotsData.slots);
  const availableDates = Object.keys(dateGroups);
  const slotsForDate = selectedDate ? (dateGroups[selectedDate] ?? []) : [];

  const handleSelectService = (service: Kudable.Service) => {
    setSelectedService(service);
    setSelectedDate(null);
    setSelectedSlot(null);
    setStep('slot');
  };

  const handleSelectDate = (date: string) => {
    setSelectedDate(date);
    if (slotsData.full_day) {
      setSelectedSlot(dateGroups[date]?.[0] ?? null);
    } else {
      setSelectedSlot(null);
    }
  };

  const handleSubmit = (values: BookingFormValues) => {
    if (!selectedService || !selectedSlot) return;

    const parsedPrice = values.suggested_price?.trim()
      ? parseInt(values.suggested_price.trim(), 10)
      : undefined;

    const payload: Kudable.BookingPayload = {
      service_id: selectedService.id,
      booked_at: selectedSlot,
      client_name: values.client_name.trim(),
      client_phone: values.client_phone?.trim() || undefined,
      client_email: values.client_email.trim(),
      client_whatsapp: values.client_whatsapp?.trim() || undefined,
      notes: values.notes?.trim() || undefined,
      client_suggested_price:
        parsedPrice && parsedPrice > 0 ? parsedPrice : undefined,
      recurrence_rule: repeatWeekly ? 'weekly' : undefined,
    };

    return submitBooking(token, payload)
      .then((response) => {
        setCompletionCode(response.data.booking.completion_code);
        setStep('done');
      })
      .catch(() => null);
  };

  const selectedSlotWeekday = selectedSlot
    ? new Date(selectedSlot).toLocaleDateString(undefined, { weekday: 'long' })
    : null;

  const goBack = () => {
    if (step === 'slot') setStep('service');
    if (step === 'details') setStep('slot');
  };

  const stepTitles: Record<Profile.BookingStep, string> = {
    service: 'Choose a Service',
    slot: 'Pick a Time',
    details: 'Your Details',
    done: '',
  };

  return (
    <styled.Overlay onClick={onClose}>
      <GlobalScrollLock />
      <styled.Sheet onClick={(event) => event.stopPropagation()}>
        {step !== 'done' && (
          <styled.TopRow>
            {step !== 'service' ? (
              <styled.BackBtn $accent={accentColor} onClick={goBack}>
                ← Back
              </styled.BackBtn>
            ) : (
              <span />
            )}
            <styled.StepTitle>{stepTitles[step]}</styled.StepTitle>
            <styled.CloseBtn onClick={onClose}>✕</styled.CloseBtn>
          </styled.TopRow>
        )}

        {step === 'service' && (
          <styled.ServiceGrid>
            {services.map((service) => {
              const price = formatServicePrice(service, currency);
              return (
                <styled.ServiceChip
                  key={service.id}
                  $selected={selectedService?.id === service.id}
                  $accent={accentColor}
                  onClick={() => handleSelectService(service)}
                >
                  <span style={{ fontWeight: 600 }}>{service.name}</span>
                  {price && <styled.ChipSubtext>{price}</styled.ChipSubtext>}
                  {service.price_note && (
                    <styled.ChipNote>{service.price_note}</styled.ChipNote>
                  )}
                </styled.ServiceChip>
              );
            })}
          </styled.ServiceGrid>
        )}

        {step === 'slot' && slotsLoading && (
          <PageLoader message="Loading slots…" />
        )}

        {step === 'slot' && !slotsLoading && (
          <>
            {availableDates.length === 0 ? (
              <EmptyState
                icon="📅"
                title="No available slots"
                body="The provider hasn't set up a schedule yet. Check back soon."
              />
            ) : (
              <>
                <styled.SectionLabel>Select a date</styled.SectionLabel>
                <styled.DateList>
                  {availableDates.map((date) => (
                    <styled.DateBtn
                      key={date}
                      $selected={selectedDate === date}
                      $accent={accentColor}
                      onClick={() => handleSelectDate(date)}
                    >
                      {fmtDate(dateGroups[date][0])}
                    </styled.DateBtn>
                  ))}
                </styled.DateList>

                {selectedDate && !slotsData.full_day && (
                  <>
                    <styled.SectionLabel>Select a time</styled.SectionLabel>
                    <styled.SlotGrid>
                      {slotsForDate.map((slot) => (
                        <styled.SlotBtn
                          key={slot}
                          $selected={selectedSlot === slot}
                          $accent={accentColor}
                          onClick={() => setSelectedSlot(slot)}
                        >
                          {fmtTime(slot)}
                        </styled.SlotBtn>
                      ))}
                    </styled.SlotGrid>
                  </>
                )}

                <styled.PrimaryBtn
                  $accent={accentColor}
                  onClick={() => selectedSlot && setStep('details')}
                  disabled={!selectedSlot}
                >
                  Continue
                </styled.PrimaryBtn>
              </>
            )}
          </>
        )}

        {step === 'details' && (
          <Form form={form} onSubmit={handleSubmit}>
            {selectedService &&
              (() => {
                const price = formatServicePrice(selectedService, currency);
                return price ? (
                  <styled.ServicePriceBox $accent={accentColor}>
                    <styled.ServicePriceLabel $accent={accentColor}>
                      {selectedService.name}
                    </styled.ServicePriceLabel>
                    <styled.ServicePriceValue $accent={accentColor}>
                      {price}
                    </styled.ServicePriceValue>
                    {selectedService.price_note && (
                      <styled.ServicePriceNote>
                        {selectedService.price_note}
                      </styled.ServicePriceNote>
                    )}
                  </styled.ServicePriceBox>
                ) : null;
              })()}

            <FormField
              name="suggested_price"
              type="number"
              inputMode="numeric"
              label="Suggest a different price (optional)"
              placeholder="e.g. 150"
            />
            <FormField
              name="client_name"
              label="Full Name *"
              placeholder="Your name"
            />
            <FormField
              name="client_phone"
              type="tel"
              label="Phone Number (optional)"
              placeholder="+27 82 000 0000"
            />
            <FormField
              name="client_email"
              type="email"
              label="Email *"
              placeholder="you@example.com"
            />
            <FormField
              name="client_whatsapp"
              type="tel"
              label="WhatsApp Number (optional)"
              placeholder="+27 82 000 0000"
            />
            <FormTextArea
              name="notes"
              label="Notes (optional)"
              placeholder="Any special requests..."
            />

            {selectedSlotWeekday && (
              <styled.RepeatRow
                as="label"
                $accent={accentColor}
                $selected={repeatWeekly}
              >
                <input
                  type="checkbox"
                  checked={repeatWeekly}
                  onChange={(event) => setRepeatWeekly(event.target.checked)}
                />
                <styled.RepeatCopy>
                  <styled.RepeatTitle>
                    Repeat every {selectedSlotWeekday}
                  </styled.RepeatTitle>
                  <styled.RepeatSub>
                    We'll hold this time every week until you cancel.
                  </styled.RepeatSub>
                </styled.RepeatCopy>
              </styled.RepeatRow>
            )}

            <styled.PrimaryBtn
              type="submit"
              $accent={accentColor}
              disabled={form.formState.isSubmitting}
            >
              {form.formState.isSubmitting ? 'Confirming…' : 'Confirm Booking'}
            </styled.PrimaryBtn>
          </Form>
        )}

        {step === 'done' && (
          <styled.DoneWrap>
            <styled.ConfirmIcon $accent={accentColor}>✓</styled.ConfirmIcon>
            <styled.ConfirmTitle>Request Sent!</styled.ConfirmTitle>
            <styled.ConfirmText>
              Your <strong>{selectedService?.name}</strong> request for{' '}
              <strong>{selectedSlot && fmtDate(selectedSlot)}</strong>
              {!slotsData.full_day && (
                <>
                  {' '}
                  at <strong>{selectedSlot && fmtTime(selectedSlot)}</strong>
                </>
              )}{' '}
              has been sent.
              {repeatWeekly && selectedSlotWeekday && (
                <>
                  {' '}
                  This slot will repeat every{' '}
                  <strong>{selectedSlotWeekday}</strong> until you cancel.
                </>
              )}{' '}
              You'll hear back from the provider shortly.
            </styled.ConfirmText>
            {completionCode && (
              <styled.CodeBox $accent={accentColor}>
                <styled.CodeLabel $accent={accentColor}>
                  Your completion code
                </styled.CodeLabel>
                <styled.CodeValue $accent={accentColor}>
                  {completionCode}
                </styled.CodeValue>
                <styled.CodeHint>
                  Show this code to your provider when the job is done — it
                  earns you a stronger "Job Confirmed" review badge.
                </styled.CodeHint>
              </styled.CodeBox>
            )}
            <styled.PrimaryBtn $accent={accentColor} onClick={onClose}>
              Done
            </styled.PrimaryBtn>
          </styled.DoneWrap>
        )}
      </styled.Sheet>
    </styled.Overlay>
  );
};
